// src/components/modal/MenuItemDetails/hooks/useMenuItemDetailsModal.ts
import { useState, useEffect, useCallback, useRef, useMemo, useTransition } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useBranch } from "~/contexts/BranchContext";
import { useShoppingCart } from "~/contexts/ShoppingCartContext";
import { supabase } from "~/utils/supabaseClient";
import { getItemName, getItemDescription, getFormName } from "~/utils/menuItemHelper";
import type { MenuItem } from "~/types/new_types";
import type { ItemCard, TabType } from "../types";
import { 
  loadItemCardsFromSession, 
  removeItemFromSession,
  saveItemCardsToSession 
} from "../utils/sessionUtils";
import { useBodyScrollLock } from "~/hooks/useBodyScrollLock";
import { 
  getTotalCardQuantity, 
  extractVariantsFromCards 
} from "../utils/variantUtils";
import { 
  calculateTotalPrice,
  calculateBasePrice 
} from "../utils/priceUtils";

interface UseMenuItemDetailsModalProps {
  item: MenuItem;
  show: boolean;
  close: () => void;
}

export function useMenuItemDetailsModal({
  item,
  show,
  close,
}: UseMenuItemDetailsModalProps) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const { restaurantId } = useParams<{ restaurantId: string }>();
  const { branch, getConvertedPrice } = useBranch();
  const {
    getTempQuantity,
    setTempQuantity,
    addToCart,
    addToCartWithVariants,
    isCartDisabled,
  } = useShoppingCart();

  const effectiveRestaurantId = restaurantId || branch?.id;
  const contentAreaRef = useRef<HTMLDivElement>(null);

  // State
  const [selectedTab, setSelectedTab] = useState<TabType>("details");
  const [itemCards, setItemCards] = useState<ItemCard[]>([]);
  const [showVariantPopup, setShowVariantPopup] = useState(false);
  const [selectedCardIndex, setSelectedCardIndex] = useState<number | null>(null);
  const [popupQuantity, setPopupQuantity] = useState(1);
  const [isLoadingExistingData, setIsLoadingExistingData] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  // Track if user made actual modifications (not just loading existing data)
  const [hasActualModifications, setHasActualModifications] = useState(false);
  const [imageHeight, setImageHeight] = useState(160);
  const [isSmallScreen, setIsSmallScreen] = useState(
    typeof window !== "undefined" ? window.innerWidth < 550 : false
  );
  const [startTime, setStartTime] = useState<number | null>(null);

  // Derived values
  const currentQuantity = getTempQuantity(item.id);
  const [nonVariantQuantity, setNonVariantQuantity] = useState<number>(
    currentQuantity > 0 ? currentQuantity : 1
  );
  const hasVariants = Boolean(
    item.variants_and_options && item.variants_and_options.length > 0
  );

  const hasVariantsOrCombos = Boolean(
    (item.variants_and_options && item.variants_and_options.length > 0) ||
    (item.meals_and_combos && item.meals_and_combos.length > 0)
  );

  const itemAlreadyAdded =
    currentQuantity > 0 || hasUserInteracted || itemCards.length > 0;
  const shouldShowAddButton = !isCartDisabled && !itemAlreadyAdded;

  const displayPrice = getConvertedPrice(item.price);
  const displayDiscountedPrice = item.discounted_price
    ? getConvertedPrice(item.discounted_price)
    : null;

  const nonVariantDisplayQuantity = hasVariants
    ? Math.max(currentQuantity, 1)
    : hasUserInteracted
    ? Math.max(nonVariantQuantity, 1)
    : 1;

  const totalCardQuantity = useMemo(() => getTotalCardQuantity(itemCards), [itemCards]);

  // Price calculations
  const totalPrice = useMemo(() => {
    return calculateTotalPrice(
      item,
      itemCards,
      hasVariants,
      hasUserInteracted,
      nonVariantQuantity,
      currentQuantity
    );
  }, [item, itemCards, hasVariants, hasUserInteracted, nonVariantQuantity, currentQuantity]);

  const basePrice = useMemo(() => {
    return calculateBasePrice(item, itemCards);
  }, [item, itemCards]);

  // Analytics upload
  const uploadData = useCallback(
    async (item_id: string, time_spent: number, branchId: string) => {
      try {
        const result = await supabase.rpc("increment_item_clicks", {
          json_data: { item_id, click: true, time_spent, branch_id: branchId },
        });
        if (result.error) throw result.error;
      } catch (err) {
        console.error("❌ uploadData error:", err);
      }
    },
    []
  );

  // Close handler with analytics and draft save
  const handleClose = useCallback(() => {
    // Track analytics
    if (startTime && item?.id) {
      const endTime = Date.now();
      const deltaTime = (endTime - startTime) / 1000;
      uploadData(item.id, deltaTime, branch?.id || "");
    }
    
    // Auto-save draft to session ONLY if user made actual modifications (not just opened modal)
    if (hasActualModifications && itemCards.length > 0) {
      const totalQuantity = getTotalCardQuantity(itemCards);
      
      // Build card data for session storage (draft save)
      const cardsData = itemCards.map((card) => {
        const cardVariants: any[] = [];

        if (item.variants_and_options && Array.isArray(item.variants_and_options)) {
          item.variants_and_options.forEach((group) => {
            const selectedValue = card.variants[group.id];
            if (selectedValue && Array.isArray((group as any).data)) {
              const groupData = (group as any).data;
              if (typeof selectedValue === 'string') {
                const selectedOption = groupData.find((opt: any) => opt.name === selectedValue);
                if (selectedOption) {
                  cardVariants.push({
                    name: getFormName(group, i18n.language) || 'Options',
                    key: group.id,
                    variantType: 'variants_and_options',
                    actionType: (group as any).action_type || 'selection',
                    children: [{
                      name: selectedOption.name,
                      price: selectedOption.price || 0,
                      imageUrl: selectedOption.image_url || '',
                    }],
                    quantity: card.quantity,
                  });
                }
              } else if (Array.isArray(selectedValue)) {
                const children = selectedValue.map((val) => {
                  const opt = groupData.find((o: any) => o.name === val);
                  return opt ? {
                    name: opt.name,
                    price: opt.price || 0,
                    imageUrl: opt.image_url || '',
                  } : null;
                }).filter(Boolean);
                if (children.length > 0) {
                  cardVariants.push({
                    name: getFormName(group, i18n.language) || 'Options',
                    key: group.id,
                    variantType: 'variants_and_options',
                    actionType: (group as any).action_type || 'selection',
                    children,
                    quantity: card.quantity,
                  });
                }
              }
            }
          });
        }

        if (item.meals_and_combos && Array.isArray(item.meals_and_combos)) {
          item.meals_and_combos.forEach((combo) => {
            const selectedCombo = card.variants[combo.id];
            if (selectedCombo) {
              cardVariants.push({
                name: getFormName(combo, i18n.language),
                key: combo.id,
                variantType: 'meals_and_combos',
                actionType: (combo as any).action_type || 'upgrade',
                children: [{
                  name: getFormName(combo, i18n.language),
                  price: (combo.data as any)?.combo_price || 0,
                  imageUrl: '',
                  comboData: combo.data,
                }],
                quantity: card.quantity,
              });
            }
          });
        }

        return {
          cardId: card.id,
          cardName: card.name,
          quantity: card.quantity,
          variants: cardVariants,
          isAiSuggestion: false,
        };
      });

      const itemData = {
        itemId: item.id,
        itemName: getItemName(item, i18n.language),
        itemDescription: getItemDescription(item, i18n.language),
        itemImage: item.image_url || '',
        basePrice: item.discounted_price || item.price,
        totalQuantity,
        cards: cardsData,
        timestamp: Date.now(),
        isDraft: true, // Mark as draft (not yet added to cart)
      };

      saveItemCardsToSession(item.id, itemData);
    }
    
    setStartTime(null);
    close();
  }, [startTime, item, branch?.id, uploadData, close, hasActualModifications, itemCards, i18n.language]);

  // Card management
  const addCard = useCallback(() => {
    const cardNumber = itemCards.length + 1;
    const newCard: ItemCard = {
      id: `${item.id}-${Date.now()}`,
      name: `${getItemName(item, i18n.language)} #${cardNumber}`,
      variants: {},
      quantity: 1,
    };
    setItemCards((prev) => [...prev, newCard]);
    setHasUserInteracted(true);
    setHasActualModifications(true);
  }, [item, i18n.language, itemCards.length]);

  const removeCard = useCallback((index: number) => {
    setItemCards((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const removeLastCard = useCallback(() => {
    if (itemCards.length === 0) return;
    
    const lastCardIndex = itemCards.length - 1;
    const lastCard = itemCards[lastCardIndex];

    if (lastCard && lastCard.quantity > 1) {
      setItemCards((prev) =>
        prev.map((card, index) =>
          index === lastCardIndex
            ? { ...card, quantity: card.quantity - 1 }
            : card
        )
      );
    } else {
      setItemCards((prev) => prev.slice(0, -1));
    }
  }, [itemCards]);

  const updateCardVariant = useCallback(
    (cardIndex: number, optionKey: string, value: any) => {
      setItemCards((prev) =>
        prev.map((card, index) =>
          index === cardIndex
            ? { ...card, variants: { ...card.variants, [optionKey]: value } }
            : card
        )
      );
      setHasUserInteracted(true);
    setHasActualModifications(true);
    },
    []
  );

  const updateCardQuantity = useCallback(
    (cardIndex: number, quantity: number) => {
      if (quantity <= 0) {
        setItemCards((prev) => prev.filter((_, index) => index !== cardIndex));
      } else {
        setItemCards((prev) =>
          prev.map((card, index) =>
            index === cardIndex ? { ...card, quantity } : card
          )
        );
      }
      setHasUserInteracted(true);
    },
    []
  );

  const clearCards = useCallback(() => {
    setItemCards([]);
    setHasUserInteracted(false);
  }, []);

  // Quantity handlers
  const handleFirstAdd = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!item.is_available || isCartDisabled) return;

      setHasUserInteracted(true);

      if (!hasVariants) {
        setNonVariantQuantity(1);
        return;
      }

      addCard();
    },
    [item.is_available, isCartDisabled, hasVariants, addCard]
  );

  const handleQuantityDecrease = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!item.is_available) return;

      // For non-variant items, just track local quantity
      if (!hasVariants) {
        if (nonVariantQuantity <= 1) {
          // Reset to initial state (show Add button)
          setHasUserInteracted(false);
          setNonVariantQuantity(1);
        } else {
          setNonVariantQuantity((prev) => Math.max(prev - 1, 1));
        }
        return;
      }

      // For variant items, remove the last card locally
      if (itemCards.length <= 1) {
        // If only 1 card left, reset to initial state
        setItemCards([]);
        setHasUserInteracted(false);
      } else {
        // Remove last card
        removeLastCard();
      }
    },
    [item.is_available, hasVariants, nonVariantQuantity, itemCards.length, removeLastCard]
  );

  const handleQuantityIncrease = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!item.is_available) return;

      // For non-variant items, just track local quantity
      if (!hasVariants) {
        if (!hasUserInteracted) {
          setHasUserInteracted(true);
          setNonVariantQuantity(1);
        } else {
          setNonVariantQuantity((prev) => prev + 1);
        }
        return;
      }

      // For variant items, add a new card locally
      setHasUserInteracted(true);
      addCard();
    },
    [item.is_available, hasVariants, hasUserInteracted, addCard]
  );

  const handleCardClick = useCallback((cardIndex: number) => {
    setSelectedCardIndex(cardIndex);
    setPopupQuantity(1);
    setShowVariantPopup(true);
  }, []);

  // Popup handlers
  const handlePopupQuantityIncrease = useCallback(() => {
    setPopupQuantity((prev) => prev + 1);
  }, []);

  const handlePopupQuantityDecrease = useCallback(() => {
    setPopupQuantity((prev) => Math.max(1, prev - 1));
  }, []);

  const handlePopupDone = useCallback(() => {
    if (selectedCardIndex !== null) {
      const isFirstCustomization = currentQuantity === 0;
      if (isFirstCustomization) {
        const variants = extractVariantsFromCards(itemCards, item, i18n.language);
        setTempQuantity(item.id, popupQuantity, variants);
        setItemCards((prev) =>
          prev.map((card, index) =>
            index === selectedCardIndex
              ? { ...card, quantity: popupQuantity }
              : card
          )
        );
      } else if (popupQuantity > 1) {
        setItemCards((prev) =>
          prev.map((card, index) =>
            index === selectedCardIndex
              ? { ...card, quantity: popupQuantity }
              : card
          )
        );
      }
    }
    setShowVariantPopup(false);
    setPopupQuantity(1);
  }, [selectedCardIndex, currentQuantity, popupQuantity, itemCards, item, i18n.language, setTempQuantity]);

  // Save handler
  const handleSave = useCallback(() => {
    if (!item.is_available || isCartDisabled || !effectiveRestaurantId) {
      return;
    }

    if (hasVariants && itemCards.length > 0) {
      const variants = extractVariantsFromCards(itemCards, item, i18n.language);
      const totalQuantity = getTotalCardQuantity(itemCards);

      // Build card data for session storage
      const cardsData = itemCards.map((card, index) => {
        const cardVariants: any[] = [];

        // Extract variants for this card
        if (item.variants_and_options && Array.isArray(item.variants_and_options)) {
          item.variants_and_options.forEach((group) => {
            const selectedValue = card.variants[group.id];
            if (selectedValue && Array.isArray((group as any).data)) {
              const groupData = (group as any).data;
              if (typeof selectedValue === 'string') {
                const selectedOption = groupData.find((opt: any) => opt.name === selectedValue);
                if (selectedOption) {
                  cardVariants.push({
                    name: getFormName(group, i18n.language) || 'Options',
                    key: group.id,
                    variantType: 'variants_and_options',
                    actionType: (group as any).action_type || 'selection',
                    children: [{
                      name: selectedOption.name,
                      price: selectedOption.price || 0,
                      imageUrl: selectedOption.image_url || '',
                    }],
                    quantity: card.quantity,
                  });
                }
              } else if (Array.isArray(selectedValue)) {
                const children = selectedValue.map((val) => {
                  const opt = groupData.find((o: any) => o.name === val);
                  return opt ? {
                    name: opt.name,
                    price: opt.price || 0,
                    imageUrl: opt.image_url || '',
                  } : null;
                }).filter(Boolean);
                if (children.length > 0) {
                  cardVariants.push({
                    name: getFormName(group, i18n.language) || 'Options',
                    key: group.id,
                    variantType: 'variants_and_options',
                    actionType: (group as any).action_type || 'selection',
                    children,
                    quantity: card.quantity,
                  });
                }
              }
            }
          });
        }

        // Handle meals_and_combos
        if (item.meals_and_combos && Array.isArray(item.meals_and_combos)) {
          item.meals_and_combos.forEach((combo) => {
            const selectedCombo = card.variants[combo.id];
            if (selectedCombo) {
              cardVariants.push({
                name: getFormName(combo, i18n.language),
                key: combo.id,
                variantType: 'meals_and_combos',
                actionType: (combo as any).action_type || 'upgrade',
                children: [{
                  name: getFormName(combo, i18n.language),
                  price: (combo.data as any)?.combo_price || 0,
                  imageUrl: '',
                  comboData: combo.data,
                }],
                quantity: card.quantity,
              });
            }
          });
        }

        return {
          cardId: card.id,
          cardName: card.name,
          quantity: card.quantity,
          variants: cardVariants,
          isAiSuggestion: false,
        };
      });

      const itemData = {
        itemId: item.id,
        itemName: getItemName(item, i18n.language),
        itemDescription: getItemDescription(item, i18n.language),
        itemImage: item.image_url || '',
        basePrice: item.discounted_price || item.price,
        totalQuantity,
        cards: cardsData,
        timestamp: Date.now(),
      };

      saveItemCardsToSession(item.id, itemData);
      setTempQuantity(item.id, totalQuantity, variants);
      addToCartWithVariants(item, effectiveRestaurantId, totalQuantity, variants, undefined, false);
    } else if (!hasVariants && hasUserInteracted) {
      const totalQuantity = nonVariantQuantity;
      
      const itemData = {
        itemId: item.id,
        itemName: getItemName(item, i18n.language),
        itemDescription: getItemDescription(item, i18n.language),
        itemImage: item.image_url || '',
        basePrice: item.discounted_price || item.price,
        totalQuantity,
        cards: [{
          cardId: `card-${Date.now()}`,
          cardName: getItemName(item, i18n.language),
          quantity: totalQuantity,
          variants: [],
          isAiSuggestion: false,
        }],
        timestamp: Date.now(),
      };

      saveItemCardsToSession(item.id, itemData);
      setTempQuantity(item.id, totalQuantity, []);
      addToCart(item, effectiveRestaurantId, totalQuantity, undefined, false);
    }

    clearCards();
    handleClose();
  }, [
    item, 
    isCartDisabled, 
    effectiveRestaurantId, 
    hasVariants, 
    hasUserInteracted,
    itemCards, 
    nonVariantQuantity,
    i18n.language,
    setTempQuantity,
    addToCart,
    addToCartWithVariants,
    clearCards,
    handleClose,
  ]);

  // ===== OPTIMIZED EFFECTS =====
  // Body scroll lock moved to component level to control execution order with back button interceptor

  // Batched initialization - combines multiple effects into one to reduce re-renders
  const [isPending, startTransition] = useTransition();
  const prevShowRef = useRef(false);

  useEffect(() => {
    // Only run when modal opens (show transitions from false to true)
    if (!show) {
      prevShowRef.current = false;
      return;
    }

    // Prevent re-running if already open
    if (prevShowRef.current) return;
    prevShowRef.current = true;

    // Batch initial state updates (single render)
    setStartTime(Date.now());
    setSelectedTab(hasVariantsOrCombos ? "variant" : "details");
    setHasActualModifications(false); // Reset modification tracking on modal open
    
    // Calculate initial height based on screen size ratio
    const isSmall = window.innerWidth < 550;
    const maxRatio = isSmall ? 0.5 : 0.33;
    const estimatedWidth = Math.min(window.innerWidth, 672); // max-w-2xl = 672px
    const initialHeight = Math.min(estimatedWidth * maxRatio, 250);
    setImageHeight(initialHeight);

    // Reset scroll position
    if (contentAreaRef.current) {
      contentAreaRef.current.scrollTop = 0;
    }

    // Defer session storage read to let modal render first
    if (item) {
      setIsLoadingExistingData(true);
      
      // Use requestAnimationFrame to defer heavy work
      requestAnimationFrame(() => {
        startTransition(() => {
          const loadedCards = loadItemCardsFromSession(item, i18n.language);
          if (loadedCards.length > 0) {
            setItemCards(loadedCards);
            setHasUserInteracted(true);
          } else {
            setItemCards([]);
            setHasUserInteracted(false);
          }
          setIsLoadingExistingData(false);

          // Handle non-variant quantity setup
          if (!hasVariants) {
            if (currentQuantity > 0) {
              setNonVariantQuantity(currentQuantity);
              setHasUserInteracted(true);
            } else {
              setNonVariantQuantity(1);
              setHasUserInteracted(false);
            }
          }
        });
      });
    }
  }, [show, item?.id]); // Minimal dependencies for initial setup

  // Scroll handler - with stable callback to prevent re-attachment
  const handleScroll = useCallback(() => {
    const contentArea = contentAreaRef.current;
    if (!contentArea) return;

    const scrollTop = contentArea.scrollTop;
    const modalWidth = contentArea.offsetWidth || 400;
    
    // Responsive aspect ratios: mobile (2:1 = 50%), desktop (3:1 = 33%)
    const isSmall = window.innerWidth < 550;
    const maxRatio = isSmall ? 0.5 : 0.33; // Max height as % of width
    const minRatio = isSmall ? 0.25 : 0.18; // Min height as % of width
    
    const maxHeight = Math.min(modalWidth * maxRatio, 250); // Cap at 250px
    const minHeight = Math.max(modalWidth * minRatio, 80);  // Floor at 80px
    const maxScroll = maxHeight;

    const newHeight = Math.max(
      minHeight,
      maxHeight - (scrollTop / maxScroll) * (maxHeight - minHeight)
    );
    setImageHeight(newHeight);
  }, []);

  useEffect(() => {
    const contentArea = contentAreaRef.current;
    if (!contentArea || !show) return;

    contentArea.addEventListener("scroll", handleScroll, { passive: true });
    return () => contentArea.removeEventListener("scroll", handleScroll);
  }, [show, handleScroll]);

  // Resize handler - only attach once
  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 550);
    };
    window.addEventListener("resize", handleResize, { passive: true });
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Reset scroll on tab change
  useEffect(() => {
    if (contentAreaRef.current && show) {
      contentAreaRef.current.scrollTop = 0;
      setImageHeight(160);
    }
  }, [selectedTab]);

  return {
    // Refs
    contentAreaRef,

    // State
    selectedTab,
    setSelectedTab,
    itemCards,
    setItemCards,
    showVariantPopup,
    setShowVariantPopup,
    selectedCardIndex,
    setSelectedCardIndex,
    popupQuantity,
    setPopupQuantity,
    isLoadingExistingData,
    hasUserInteracted,
    setHasUserInteracted,
    imageHeight,
    isSmallScreen,

    // Derived values
    currentQuantity,
    nonVariantQuantity,
    setNonVariantQuantity,
    hasVariants,
    hasVariantsOrCombos,
    itemAlreadyAdded,
    shouldShowAddButton,
    displayPrice,
    displayDiscountedPrice,
    nonVariantDisplayQuantity,
    totalCardQuantity,
    effectiveRestaurantId,
    isRTL,
    totalPrice,
    basePrice,

    // Context values
    isCartDisabled,
    addToCart,
    addToCartWithVariants,
    setTempQuantity,
    branch,
    getConvertedPrice,

    // Handlers
    handleClose,
    handleFirstAdd,
    handleQuantityDecrease,
    handleQuantityIncrease,
    handleCardClick,
    handlePopupQuantityIncrease,
    handlePopupQuantityDecrease,
    handlePopupDone,
    handleSave,
    addCard,
    removeCard,
    removeLastCard,
    updateCardVariant,
    updateCardQuantity,
    clearCards,
  };
}
